// AuthContext.tsx
import React, { createContext, useContext, useState, type ReactNode, useEffect } from 'react';

import { getCookie, setCookie, removeCookie } from '@shared/lib';
import { queryClient } from '@shared';
import { logCustomEvent } from '@/shared/configs/firebase';
import { clearLegacyRefreshToken, readLegacyRefreshTokenKey } from '@/shared/api/legacy-refresh-cookie';
import { reissueSession } from '@/shared/api/reissue-session';

import { revokeRefreshTokenOnServer } from './serverLogout';

import type { UserState } from '@/entities/auth/types';
import type { UserGrade } from '@/entities/member/types';

interface AuthContextType {
  isAuthenticated: boolean | null;
  accessToken: string | null;
  email: string | null;
  userId: string | null;
  userName: string;
  userGrade: UserGrade;
  login: (loginParams: UserState) => void;
  loginRefresh: (accessToken: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userName, setUserName] = useState('');
  const [userGrade, setUserGrade] = useState<UserGrade>('BEGINNER');
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    // 비동기 함수로 로그인 상태를 체크
    const checkAuth = async () => {
      const loginUserId = getCookie('userId');
      if (loginUserId) {
        const currentAccessToken = getCookie('accessToken');

        // TRAD-1698: accessToken 만료(쿠키 소멸)됐으면 선제 갱신 — 쿼리 가드(enabled: isAuthenticated===true) 통과 후 즉시 유효한 토큰으로 요청 가능.
        // TRAD-3861 — 리프레시 키는 서버 HttpOnly 쿠키라 «살아있나»를 여기서 볼 수 없다 → userId 가 있으면 시도하고, 401·404 면 세션 끝.
        if (!currentAccessToken) {
          const isSecure = window.location.protocol === 'https:';
          // TRAD-3683 — handlers 와 같은 공용 경로(탭 간 잠금 · 다른 탭이 이미 갱신했으면 그 토큰).
          // staleAccessToken=null: accessToken 없이 시작했다 → 잠금 대기 중 다른 탭이 토큰을 저장했으면 reissue 생략.
          const result = await reissueSession({
            userId: loginUserId,
            staleAccessToken: null,
            secure: isSecure,
          });
          if (result.kind === 'FAILED') {
            // raw error 는 항상 남긴다(CLAUDE.md) — LOGOUT·KEEP 둘 다.
            console.error(`[AuthContext] 선제 reissue 실패(${result.outcome}):`, result.error);
            if (result.outcome === 'LOGOUT') {
              // 401·404 = 세션 끝 → 종전처럼 쿠키 제거 + 비로그인(리프레시는 서버 쿠키 — 옛 JS 쿠키만 정리).
              removeCookie('accessToken');
              clearLegacyRefreshToken();
              removeCookie('userId');
              removeCookie('email');
              removeCookie('userGrade');
              setIsAuthenticated(false);
              return;
            }
            // TRAD-3683 — ~~갱신 실패 = 무조건 로그아웃~~ → 네트워크·5xx·403(KEEP)은 쿠키를 남기고 로그인 상태로 둔다
            // (팀장 결정 2026-10-11 · iOS «기타 오류 → 토큰 유지»와 같은 선 · «모름» 상태는 두지 않는다).
            // accessToken 이 없으니 첫 인증 요청이 401 → handlers 가 다시 갱신을 시도한다.
          }
        }

        setIsAuthenticated(true);
        setUserId(loginUserId);
        setEmail(getCookie('email'));
        // TRAD-2167 — 새로고침 후 등급이 초기값 'BEGINNER' 로 남던 것을 씨앗으로 복원한다.
        // 서버 기준 값은 `useUserGrade()` 가 별도로 가져와 덮어쓴다.
        setUserGrade((getCookie('userGrade') as UserGrade) ?? 'BEGINNER');
        setAccessToken(getCookie('accessToken'));
      } else {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  // 재발급 실패(세션 끝) 시 자동 로그아웃
  useEffect(() => {
    const handleTokenExpired = () => logout();
    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => window.removeEventListener('auth:token-expired', handleTokenExpired);
  }, []);

  const login = ({ email, userId, userName, userGrade, accessToken }: UserState) => {
    setEmail(email);
    setUserId(userId);
    setAccessToken(accessToken);
    setIsAuthenticated(true);
    setUserName(userName);
    setUserGrade(userGrade);

    const isSecure = window.location.protocol === 'https:';
    // TRAD-1676: maxAge 추가 — 브라우저 재시작 후에도 세션 유지
    //   access:  15분 (backend TTL과 정합)
    //   refresh: 30일 (backend TTL과 정합)
    //   email/userId: refresh와 동일 30일
    setCookie('email', email, {
      path: '/',
      secure: isSecure,
      maxAge: 60 * 60 * 24 * 30,
    });
    setCookie('userId', userId, {
      path: '/',
      secure: isSecure,
      maxAge: 60 * 60 * 24 * 30,
    });
    /* TRAD-2167 — 등급 **씨앗**만 저장한다. 진실이 아니다.
       새로고침 직후 `useUserGrade()` 가 서버 응답을 받기 전까지의 깜빡임을 막는 용도다.
       🔴 이 값을 진실로 쓰면 안 된다 — 등급은 포인트로 계속 올라가는데 쿠키는 재로그인
       전까지 안 바뀌므로, **승급한 사용자가 낡은 Beginner 로 막히는** 방향으로 틀린다.
       그래서 훅이 서버 값을 받는 즉시 덮어쓴다. */
    setCookie('userGrade', userGrade, {
      path: '/',
      secure: isSecure,
      maxAge: 60 * 60 * 24 * 30,
    });
    setCookie('accessToken', accessToken, {
      path: '/',
      secure: isSecure,
      maxAge: 60 * 15,
    });
    // TRAD-3861 — ~~리프레시 키 JS 쿠키(30일) 저장~~ 삭제. 키는 로그인 응답의 Set-Cookie(HttpOnly · Path=/api/auth)로만 산다.
    // 1단계 창에선 응답 본문에 refreshTokenKey 가 와도 JS 쿠키에 쓰지 않는다(계약 §2). 옛 번들이 남긴 JS 쿠키가 있으면 여기서 지운다.
    clearLegacyRefreshToken();

    // 계정 전환 시 stale 캐시로 인한 personalization 오표시 방지
    queryClient.removeQueries({ queryKey: ['polymarket'] });
    // 로그인 후 auth-sensitive query 갱신 (logout의 queryClient.clear()와 대칭)
    queryClient.invalidateQueries();
  };

  const loginRefresh = (newAccessToken: string) => {
    setAccessToken(newAccessToken);
    setCookie('accessToken', newAccessToken, {
      path: '/',
      secure: window.location.protocol === 'https:',
      maxAge: 60 * 15,
    });
  };

  const logout = () => {
    logCustomEvent('logout');

    // TRAD-3590 — 서버 세션 폐기를 **쿠키를 지우기 전에** 시작한다(키는 쿠키에서 읽는다).
    // 기다리지 않는다(fire-and-forget + 3초 타임아웃): 서버가 느리거나 실패해도 로그아웃 버튼은 멈추지 않고 로컬 로그아웃이 진행된다.
    // 로그인 흔적(userId)이 없으면 호출하지 않는다. 강제 로그아웃('auth:token-expired')은 재발급 실패 쪽 핸들러가 이미 쿠키를
    // 지운 뒤라 키가 없어 여기서 자연히 건너뛴다(그 키는 재발급이 거절한 키다).
    // TRAD-3861 — 키는 서버 HttpOnly 쿠키가 자동 동봉한다 → 로그인 흔적(userId)만 있으면 호출. 옛 JS 쿠키 키가 남아 있으면 그것도 body 로(이행기).
    if (getCookie('userId')) {
      revokeRefreshTokenOnServer(readLegacyRefreshTokenKey()).catch(error => {
        console.error('[AuthContext] server-side logout failed; local logout proceeded', error);
      });
    }

    removeCookie('accessToken');
    clearLegacyRefreshToken();
    removeCookie('userId');
    removeCookie('email');
    removeCookie('userGrade');
    setEmail(null);
    setUserId(null);
    setAccessToken(null);
    setIsAuthenticated(false);

    // React Query 캐시 초기화 (polymarket 포함 전체 제거)
    queryClient.removeQueries({ queryKey: ['polymarket'] });
    queryClient.clear();

    // Google OAuth 자동 로그인 비활성화
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        accessToken,
        userId,
        email,
        userName,
        userGrade,
        login,
        loginRefresh,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
